import { useContext } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { datadogRum } from '@datadog/browser-rum'
import { QueryClientContext } from '@tanstack/react-query'
import { ErrorBoundary as ReactErrorBoundary, type FallbackProps } from 'react-error-boundary'

import { isDatadogRUMEnabled } from '@/lib/datadog'

const ErrorContainer = styled(Box, {
	shouldForwardProp: (p) => p !== 'fullPage',
})<{ fullPage?: boolean }>(({ theme, fullPage }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	minHeight: fullPage ? '100vh' : 'auto',
	gap: theme.spacing(2),
	padding: theme.spacing(3),
}))

const DevErrorBox = styled(Box)(({ theme }) => ({
	marginTop: theme.spacing(2),
	padding: theme.spacing(2),
	backgroundColor: theme.palette.grey[100],
	borderRadius: theme.shape.borderRadius,
	fontFamily: 'monospace',
	fontSize: 12,
	maxWidth: '100%',
	overflow: 'auto',
	whiteSpace: 'pre-wrap',
}))

export interface ErrorFallbackProps extends FallbackProps {
	fullPage?: boolean
}

export function ErrorFallback({ error, resetErrorBoundary, fullPage }: ErrorFallbackProps) {
	const queryClient = useContext(QueryClientContext)

	function handleReset() {
		if (queryClient) void queryClient.resetQueries()
		resetErrorBoundary()
	}

	return (
		<ErrorContainer
			fullPage={fullPage}
			role={fullPage ? 'alertdialog' : 'alert'}
			aria-labelledby={fullPage ? 'error-boundary-heading' : undefined}
			aria-describedby={fullPage ? 'error-boundary-description' : undefined}
		>
			<Typography
				variant="h5"
				id={fullPage ? 'error-boundary-heading' : undefined}
				sx={{ fontWeight: 'bold' }}
			>
				Something went wrong
			</Typography>
			<Typography
				id={fullPage ? 'error-boundary-description' : undefined}
				variant="body1"
				color="text.secondary"
				sx={{ textAlign: 'center', maxWidth: 480 }}
			>
				Please refresh the page. If the problem persists, contact support.
			</Typography>
			<Button variant="contained" onClick={handleReset}>
				Try again
			</Button>
			{import.meta.env.DEV && <DevErrorBox>{String(error)}</DevErrorBox>}
		</ErrorContainer>
	)
}

interface ErrorBoundaryProps {
	children: React.ReactNode
	fullPage?: boolean
	renderError?: (error: Error) => React.ReactNode | null
}

export function ErrorBoundary({ children, fullPage, renderError }: ErrorBoundaryProps) {
	return (
		<ReactErrorBoundary
			fallbackRender={({ error, resetErrorBoundary }) => {
				const custom = renderError?.(error as Error)
				if (custom != null) return <>{custom}</>
				return (
					<ErrorFallback
						error={error}
						resetErrorBoundary={resetErrorBoundary}
						fullPage={fullPage}
					/>
				)
			}}
			onError={(error, info) => {
				const isHandled = renderError?.(error as Error) != null
				if (!isHandled && isDatadogRUMEnabled()) {
					datadogRum.addError(error, { componentStack: info.componentStack })
				}
			}}
		>
			{children}
		</ReactErrorBoundary>
	)
}
